import { useState, useEffect, useCallback } from 'react';
import { useActivityLog } from '@/hooks/useActivityLog';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const EDGE_URL = `${SUPABASE_URL}/functions/v1/setup-admin-account`;

export type AdminPermission = 'full' | 'orders' | 'products';

export interface AdminUser {
  id: string;
  email: string;
  admin_name: string;
  permissions: AdminPermission;
  created_at: string;
  last_sign_in_at: string | null;
}

interface AdminsManagerProps {
  currentUserEmail: string;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

type ModalMode = 'add' | 'edit' | null;

const PERMISSION_OPTIONS: {
  value: AdminPermission;
  label: string;
  desc: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
  tabs: string[];
}[] = [
  {
    value: 'full',
    label: 'مشرف كامل',
    desc: 'صلاحية الوصول لجميع أقسام لوحة التحكم',
    icon: 'ri-shield-star-line',
    color: 'text-[#1a1a1a]',
    bg: 'bg-[#1a1a1a]',
    border: 'border-[#1a1a1a]',
    tabs: ['dashboard','products','orders','customers','reports','discounts','deals','cities','badges','reviews','whatsapp','settings','admins','account'],
  },
  {
    value: 'orders',
    label: 'مشرف طلبات',
    desc: 'الطلبات والعملاء فقط',
    icon: 'ri-file-list-3-line',
    color: 'text-emerald-700',
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    tabs: ['orders','customers','account'],
  },
  {
    value: 'products',
    label: 'مشرف منتجات',
    desc: 'المنتجات والمخزون والعروض فقط',
    icon: 'ri-shopping-bag-3-line',
    color: 'text-amber-700',
    bg: 'bg-amber-500',
    border: 'border-amber-500',
    tabs: ['products','deals','badges','reviews','account'],
  },
];

export function getPermissionInfo(p: AdminPermission) {
  return PERMISSION_OPTIONS.find(o => o.value === p) ?? PERMISSION_OPTIONS[0];
}

export function getAllowedTabs(p: AdminPermission): string[] {
  return getPermissionInfo(p).tabs;
}

function PermissionBadge({ permission }: { permission: AdminPermission }) {
  const info = getPermissionInfo(permission);
  const badgeStyles: Record<AdminPermission, string> = {
    full: 'bg-gray-100 text-gray-700 border-gray-200',
    orders: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    products: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${badgeStyles[permission]}`}>
      <i className={`${info.icon} text-xs`}></i>
      {info.label}
    </span>
  );
}

export default function AdminsManager({ currentUserEmail, onToast }: AdminsManagerProps) {
  const { logActivity } = useActivityLog();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formConfirm, setFormConfirm] = useState('');
  const [formPermission, setFormPermission] = useState<AdminPermission>('full');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list_admins' }),
      });
      const data = await res.json();
      if (data.admins) setAdmins(data.admins);
    } catch {
      onToast('حدث خطأ أثناء تحميل المشرفين', 'error');
    }
    setLoading(false);
  }, [onToast]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const openAdd = () => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormConfirm('');
    setFormPermission('full');
    setErrors({});
    setShowPassword(false);
    setShowConfirm(false);
    setSelectedAdmin(null);
    setModalMode('add');
  };

  const openEdit = (admin: AdminUser) => {
    setFormName(admin.admin_name);
    setFormEmail(admin.email);
    setFormPassword('');
    setFormConfirm('');
    setFormPermission(admin.permissions ?? 'full');
    setErrors({});
    setShowPassword(false);
    setShowConfirm(false);
    setSelectedAdmin(admin);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedAdmin(null);
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formName.trim()) errs.name = 'يرجى إدخال اسم المشرف';
    if (modalMode === 'add') {
      if (!formEmail.trim()) errs.email = 'يرجى إدخال البريد الإلكتروني';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) errs.email = 'البريد الإلكتروني غير صحيح';
      if (!formPassword) errs.password = 'يرجى إدخال كلمة المرور';
      else if (formPassword.length < 8) errs.password = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
      if (formPassword !== formConfirm) errs.confirm = 'كلمتا المرور غير متطابقتين';
    } else {
      if (formPassword && formPassword.length < 8) errs.password = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
      if (formPassword && formPassword !== formConfirm) errs.confirm = 'كلمتا المرور غير متطابقتين';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        const res = await fetch(EDGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'add_admin',
            email: formEmail.trim(),
            password: formPassword,
            adminName: formName.trim(),
            permissions: formPermission,
          }),
        });
        const data = await res.json();
        if (data.error) {
          onToast(data.error === 'البريد الإلكتروني مستخدم بالفعل' ? data.error : 'حدث خطأ أثناء الإضافة', 'error');
        } else {
          onToast('تم إضافة المشرف بنجاح ✓');
          logActivity({
            actionType: 'admin_add',
            entityType: 'admin',
            entityName: formName.trim() || formEmail.trim(),
            adminEmail: currentUserEmail,
            adminName: currentUserEmail.split('@')[0],
            details: {
              newAdminEmail: formEmail.trim(),
              newAdminName: formName.trim(),
              permissions: formPermission,
            },
          });
          closeModal();
          fetchAdmins();
        }
      } else if (modalMode === 'edit' && selectedAdmin) {
        const res = await fetch(EDGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_admin',
            userId: selectedAdmin.id,
            adminName: formName.trim(),
            permissions: formPermission,
            ...(formPassword ? { newPassword: formPassword } : {}),
          }),
        });
        const data = await res.json();
        if (data.error) {
          onToast('حدث خطأ أثناء التعديل', 'error');
        } else {
          onToast('تم تحديث بيانات المشرف ✓');
          logActivity({
            actionType: 'admin_update',
            entityType: 'admin',
            entityId: selectedAdmin.id,
            entityName: formName.trim() || selectedAdmin.email,
            adminEmail: currentUserEmail,
            adminName: currentUserEmail.split('@')[0],
            details: {
              targetAdminEmail: selectedAdmin.email,
              updatedName: formName.trim(),
              updatedPermissions: formPermission,
              previousPermissions: selectedAdmin.permissions,
              passwordChanged: !!formPassword,
            },
          });
          closeModal();
          fetchAdmins();
        }
      }
    } catch {
      onToast('حدث خطأ، تحقق من الاتصال', 'error');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_admin', userId: deleteConfirm.id }),
      });
      const data = await res.json();
      if (data.error) {
        onToast('حدث خطأ أثناء الحذف', 'error');
      } else {
        onToast('تم حذف المشرف');
        logActivity({
          actionType: 'admin_delete',
          entityType: 'admin',
          entityId: deleteConfirm.id,
          entityName: deleteConfirm.admin_name || deleteConfirm.email,
          adminEmail: currentUserEmail,
          adminName: currentUserEmail.split('@')[0],
          details: {
            deletedAdminEmail: deleteConfirm.email,
            deletedAdminName: deleteConfirm.admin_name,
            deletedAdminPermissions: deleteConfirm.permissions,
          },
        });
        setDeleteConfirm(null);
        fetchAdmins();
      }
    } catch {
      onToast('حدث خطأ، تحقق من الاتصال', 'error');
    }
    setDeleting(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'لم يسجل دخول بعد';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getInitials = (name: string, email: string) => {
    if (name) return name.charAt(0).toUpperCase();
    return email.charAt(0).toUpperCase();
  };

  const avatarColors = [
    'bg-slate-100 text-slate-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
  ];

  const selectedPermInfo = getPermissionInfo(formPermission);

  return (
    <div className="flex flex-col gap-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {admins.length} {admins.length === 1 ? 'مشرف' : 'مشرفين'} مسجلين
        </p>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#333] text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          إضافة مشرف
        </button>
      </div>

      {/* Permission Legend */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PERMISSION_OPTIONS.map(opt => (
          <div key={opt.value} className="bg-gray-50 rounded-xl border border-gray-100 p-3 flex items-start gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${opt.bg}`}>
              <i className={`${opt.icon} text-white text-sm`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#1a1a1a]">{opt.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{opt.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Admins List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">جاري تحميل المشرفين...</p>
        </div>
      ) : admins.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl">
            <i className="ri-user-settings-line text-gray-300 text-2xl"></i>
          </div>
          <p className="text-sm text-gray-400">لا يوجد مشرفون مسجلون</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {admins.map((admin, idx) => {
            const isCurrentUser = admin.email === currentUserEmail;
            const colorClass = avatarColors[idx % avatarColors.length];
            const permInfo = getPermissionInfo(admin.permissions ?? 'full');
            return (
              <div
                key={admin.id}
                className="bg-gray-50 rounded-2xl border border-gray-100 p-4 flex items-center gap-4"
              >
                {/* Avatar */}
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 text-base font-black ${colorClass}`}>
                  {getInitials(admin.admin_name, admin.email)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-[#1a1a1a] truncate">
                      {admin.admin_name || 'بدون اسم'}
                    </p>
                    <PermissionBadge permission={admin.permissions ?? 'full'} />
                    {isCurrentUser && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        أنت
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 truncate" dir="ltr">{admin.email}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                      <i className="ri-shield-line text-xs"></i>
                      {permInfo.tabs.length} قسم متاح
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                      <i className="ri-calendar-line text-xs"></i>
                      أُضيف {formatDate(admin.created_at)}
                    </span>
                    {admin.last_sign_in_at && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-400">
                        <i className="ri-login-circle-line text-xs"></i>
                        آخر دخول {formatDate(admin.last_sign_in_at)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(admin)}
                    className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-[#1a1a1a] hover:border-gray-300 transition-colors cursor-pointer"
                    title="تعديل"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  {!isCurrentUser && (
                    <button
                      onClick={() => setDeleteConfirm(admin)}
                      className="w-9 h-9 flex items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-400 hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer"
                      title="حذف"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Security Note */}
      <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100 flex items-start gap-3">
        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-shield-keyhole-line text-[#1a1a1a] text-sm"></i>
        </div>
        <div>
          <p className="text-xs font-bold text-gray-600">ملاحظة أمنية</p>
          <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
            الصلاحيات تتحكم في الأقسام المرئية فقط. أضف فقط أشخاصاً موثوقين.
            لا يمكن حذف حسابك الخاص لضمان وجود مشرف دائماً.
          </p>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${modalMode === 'add' ? 'bg-[#1a1a1a]' : 'bg-amber-400'}`}>
                  <i className={`text-sm ${modalMode === 'add' ? 'ri-user-add-line text-white' : 'ri-user-settings-line text-black'}`}></i>
                </div>
                <h3 className="font-bold text-[#1a1a1a] text-base">
                  {modalMode === 'add' ? 'إضافة مشرف جديد' : 'تعديل بيانات المشرف'}
                </h3>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            {/* Modal Body — scrollable */}
            <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">

              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-500">اسم المشرف <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => { setFormName(e.target.value); setErrors(er => ({ ...er, name: '' })); }}
                  placeholder="مثال: أحمد محمد"
                  className={`w-full border ${errors.name ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors`}
                />
                {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
              </div>

              {/* Email — only for add */}
              {modalMode === 'add' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-500">البريد الإلكتروني <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={e => { setFormEmail(e.target.value); setErrors(er => ({ ...er, email: '' })); }}
                    placeholder="admin@example.com"
                    className={`w-full border ${errors.email ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors`}
                    dir="ltr"
                  />
                  {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                </div>
              )}

              {/* Email display for edit */}
              {modalMode === 'edit' && selectedAdmin && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-500">البريد الإلكتروني</label>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5">
                    <i className="ri-mail-line text-gray-300 text-sm"></i>
                    <span className="text-sm text-gray-400 flex-1" dir="ltr">{selectedAdmin.email}</span>
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <i className="ri-lock-line text-xs"></i>
                      لا يمكن تغييره
                    </span>
                  </div>
                </div>
              )}

              {/* ── Permissions Selector ── */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-500">نوع الصلاحية <span className="text-red-400">*</span></label>
                <div className="flex flex-col gap-2">
                  {PERMISSION_OPTIONS.map(opt => {
                    const isSelected = formPermission === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormPermission(opt.value)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                          isSelected
                            ? `${opt.border} bg-gray-50`
                            : 'border-gray-100 bg-white hover:border-gray-200'
                        }`}
                      >
                        {/* Icon */}
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${isSelected ? opt.bg : 'bg-gray-100'}`}>
                          <i className={`${opt.icon} text-sm ${isSelected ? 'text-white' : 'text-gray-400'}`}></i>
                        </div>
                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold ${isSelected ? 'text-[#1a1a1a]' : 'text-gray-600'}`}>{opt.label}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">{opt.desc}</p>
                          {/* Tabs preview */}
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {opt.tabs.filter(t => t !== 'account').map(t => {
                              const tabLabels: Record<string, string> = {
                                dashboard: 'الرئيسية', products: 'المنتجات', orders: 'الطلبات',
                                customers: 'العملاء', reports: 'التقارير', discounts: 'الخصومات',
                                deals: 'العروض', cities: 'المدن', badges: 'الشارات',
                                reviews: 'التقييمات', whatsapp: 'واتساب', settings: 'الإعدادات',
                                admins: 'المشرفون',
                              };
                              return (
                                <span key={t} className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-white border border-gray-200 text-gray-600' : 'bg-gray-50 text-gray-400'}`}>
                                  {tabLabels[t] ?? t}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        {/* Radio */}
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? `${opt.border}` : 'border-gray-300'}`}>
                          {isSelected && <div className={`w-2 h-2 rounded-full ${opt.bg}`}></div>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected summary */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 mt-1`}>
                  <i className={`${selectedPermInfo.icon} text-sm ${selectedPermInfo.color}`}></i>
                  <p className="text-xs text-gray-500">
                    سيتمكن من الوصول إلى <strong className="text-[#1a1a1a]">{selectedPermInfo.tabs.length} أقسام</strong> في لوحة التحكم
                  </p>
                </div>
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-500">
                  كلمة المرور
                  {modalMode === 'edit' && <span className="text-gray-400 font-normal"> (اتركها فارغة للإبقاء على الحالية)</span>}
                  {modalMode === 'add' && <span className="text-red-400"> *</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formPassword}
                    onChange={e => { setFormPassword(e.target.value); setErrors(er => ({ ...er, password: '' })); }}
                    placeholder={modalMode === 'edit' ? 'اتركها فارغة للإبقاء على الحالية' : '••••••••'}
                    className={`w-full border ${errors.password ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors pl-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={`text-sm ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
                {formPassword && (
                  <div className="flex items-center gap-1.5 mt-1">
                    {[1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          formPassword.length >= i * 3
                            ? formPassword.length >= 12 ? 'bg-green-500'
                              : formPassword.length >= 8 ? 'bg-amber-400'
                              : 'bg-red-400'
                            : 'bg-gray-200'
                        }`}
                      ></div>
                    ))}
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {formPassword.length >= 12 ? 'قوية جداً' : formPassword.length >= 8 ? 'جيدة' : 'ضعيفة'}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              {(modalMode === 'add' || formPassword) && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-500">
                    تأكيد كلمة المرور {modalMode === 'add' && <span className="text-red-400">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={formConfirm}
                      onChange={e => { setFormConfirm(e.target.value); setErrors(er => ({ ...er, confirm: '' })); }}
                      placeholder="أعد كتابة كلمة المرور"
                      className={`w-full border ${errors.confirm ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors pl-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`text-sm ${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                    </button>
                  </div>
                  {errors.confirm && <p className="text-red-500 text-xs">{errors.confirm}</p>}
                  {formConfirm && formPassword === formConfirm && (
                    <p className="text-green-500 text-xs flex items-center gap-1">
                      <i className="ri-checkbox-circle-line"></i> كلمتا المرور متطابقتان
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 px-6 pb-5 pt-3 border-t border-gray-50 flex-shrink-0">
              <button
                onClick={closeModal}
                disabled={saving}
                className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex-1 flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${
                  modalMode === 'add'
                    ? 'bg-[#1a1a1a] hover:bg-[#333] text-white'
                    : 'bg-amber-400 hover:bg-amber-500 text-black'
                }`}
              >
                {saving ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : modalMode === 'add' ? (
                  <><i className="ri-user-add-line"></i> إضافة المشرف</>
                ) : (
                  <><i className="ri-save-line"></i> حفظ التعديلات</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-user-unfollow-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف المشرف</h3>
            <p className="text-sm text-gray-500 text-center mb-1">هل أنت متأكد من حذف المشرف</p>
            <p className="text-sm font-bold text-[#1a1a1a] text-center mb-1">
              &quot;{deleteConfirm.admin_name || deleteConfirm.email}&quot;
            </p>
            <p className="text-xs text-gray-400 text-center mb-6">
              لن يتمكن من الدخول للوحة التحكم بعد الحذف.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : null}
                حذف المشرف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
